import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const STAFF_TOKEN_KEY = "cocoon.staff.token";
export const CUSTOMER_TOKEN_KEY = "cocoon.customer.token";

function getToken(kind) {
  if (kind === "staff") return localStorage.getItem(STAFF_TOKEN_KEY);
  if (kind === "customer") return localStorage.getItem(CUSTOMER_TOKEN_KEY);
  return null;
}

/**
 * Single-session kick-out — every axios client's response interceptor
 * watches for 401s whose body is `{code:"SESSION_INVALIDATED"}` or
 * whose response carries the `X-Session-Invalidated` header. When
 * detected we drop the stored token for that client and broadcast a
 * `cocoon-session-invalidated` window event that `AuthProvider`
 * listens to (pop the modal + logout + redirect to login). We only
 * fire when there's actually a token stored so a routine "please log
 * in" 401 on an anonymous page doesn't trigger the popup.
 */
function isSessionInvalidatedResponse(res) {
  if (!res || res.status !== 401) return false;
  const hdr = res.headers?.["x-session-invalidated"] || res.headers?.["X-Session-Invalidated"];
  if (hdr === "1" || hdr === 1) return true;
  const detail = res.data?.detail;
  if (detail && typeof detail === "object" && detail.code === "SESSION_INVALIDATED") return true;
  return false;
}

export function makeClient(kind) {
  const instance = axios.create({ baseURL: API });
  instance.interceptors.request.use((config) => {
    const t = getToken(kind);
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
  });
  instance.interceptors.response.use(
    (r) => r,
    (err) => {
      const res = err?.response;
      if (isSessionInvalidatedResponse(res) && getToken(kind)) {
        // Drop the local token so any parallel requests stop retrying
        // with a stale JWT while the modal is on screen.
        localStorage.removeItem(kind === "staff" ? STAFF_TOKEN_KEY : CUSTOMER_TOKEN_KEY);
        try {
          window.dispatchEvent(new CustomEvent("cocoon-session-invalidated", { detail: { kind } }));
        } catch (_e) { /* older browsers — ignore */ }
      }
      return Promise.reject(err);
    },
  );
  return instance;
}

export const staffApi = makeClient("staff");
export const customerApi = makeClient("customer");

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export function formatIDR(n) {
  if (typeof n !== "number") return "-";
  return "Rp " + n.toLocaleString("id-ID");
}

// ISO 4217 short list of commonly-used currencies for the Cocoon branches. Extend as
// needed; the value stored on the branch is a plain 3-letter code.
export const CURRENCY_OPTIONS = [
  { code: "IDR", label: "Indonesian Rupiah (Rp)" },
  { code: "USD", label: "US Dollar ($)" },
  { code: "EUR", label: "Euro (€)" },
  { code: "GBP", label: "British Pound (£)" },
  { code: "AUD", label: "Australian Dollar (A$)" },
  { code: "SGD", label: "Singapore Dollar (S$)" },
  { code: "MYR", label: "Malaysian Ringgit (RM)" },
  { code: "THB", label: "Thai Baht (฿)" },
  { code: "PHP", label: "Philippine Peso (₱)" },
  { code: "VND", label: "Vietnamese Dong (₫)" },
  { code: "JPY", label: "Japanese Yen (¥)" },
  { code: "KRW", label: "South Korean Won (₩)" },
  { code: "CNY", label: "Chinese Yuan (¥)" },
  { code: "HKD", label: "Hong Kong Dollar (HK$)" },
  { code: "TWD", label: "New Taiwan Dollar (NT$)" },
  { code: "INR", label: "Indian Rupee (₹)" },
  { code: "AED", label: "UAE Dirham (د.إ)" },
  { code: "SAR", label: "Saudi Riyal (﷼)" },
  { code: "BHD", label: "Bahraini Dinar (BD)" },
  { code: "CHF", label: "Swiss Franc (CHF)" },
  { code: "CAD", label: "Canadian Dollar (CA$)" },
  { code: "NZD", label: "New Zealand Dollar (NZ$)" },
  { code: "BRL", label: "Brazilian Real (R$)" },
  { code: "MXN", label: "Mexican Peso (MX$)" },
  { code: "ZAR", label: "South African Rand (R)" },
  { code: "TRY", label: "Turkish Lira (₺)" },
  { code: "RUB", label: "Russian Ruble (₽)" },
];

// Formats a money amount in the given branch's currency (fallback IDR). Uses
// Intl.NumberFormat so each currency's local grouping / symbol conventions apply
// automatically across the UI. Cocoon operates in whole-currency-units — every
// display is rounded to no decimals and shown with a thousands separator
// regardless of the currency picked.
export function formatBranchMoney(branchOrCode, amount) {
  const code = (typeof branchOrCode === "string" ? branchOrCode : branchOrCode?.currency) || "IDR";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "-";
  try {
    const locale = code === "IDR" ? "id-ID" : "en-GB";
    return new Intl.NumberFormat(locale, {
      style: "currency", currency: code,
      maximumFractionDigits: 0, minimumFractionDigits: 0,
    }).format(Math.round(n));
  } catch {
    return `${code} ${Math.round(n).toLocaleString("en-GB")}`;
  }
}

// Returns a compact "Hours" string derived from `opening_hours` (the single source
// of truth on the branch document). Determines "today" in the branch's timezone
// (defaults to Asia/Makassar) so viewers in other regions still see Bali-local
// hours. Renders "Closed today" when the weekday is flagged closed and empty
// string when opening_hours is missing entirely.
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export function formatBranchHours(branch) {
  if (!branch) return "";
  const oh = branch.opening_hours;
  if (!oh || typeof oh !== "object") return "";
  const tz = branch.timezone || "Asia/Makassar";
  let weekday;
  try {
    weekday = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short" }).format(new Date()).toLowerCase();
  } catch {
    weekday = WEEKDAY_KEYS[new Date().getDay()];
  }
  const cfg = oh[weekday];
  if (!cfg) return "";
  if (cfg.open === false) return "Closed today";
  const o = cfg.open_time || "09:00";
  const c = cfg.close_time || "21:00";
  return `${o} – ${c}`;
}
